// (0) Landing page — the front door. Explains the CWY loop in plain, warm
// language so a student understands the whole workflow before signing in.
// Lives outside the app shell (its own full-bleed brand layout).
import { useNavigate } from 'react-router-dom';
import { Button, BinPill, type DsBin } from '@/components/ds';
import { fontPx } from '@/lib/fontSize';

// The core loop, in the order a student lives it. Friendly, no jargon.
const STEPS: { n: string; title: string; body: string }[] = [
  {
    n: '1',
    title: 'Pick a paper — or just one topic',
    body: 'Sit a full board paper under real exam conditions, or drill a single shaky topic when you only have ten minutes.',
  },
  {
    n: '2',
    title: 'Answer your way',
    body: 'Type your working, or snap a photo of your handwritten answer. Our AI reads both — no special format to learn.',
  },
  {
    n: '3',
    title: 'See your marks instantly',
    body: 'No waiting on a teacher. You find out what you got right, what slipped, and exactly why — the moment you submit.',
  },
  {
    n: '4',
    title: 'Land in a colour',
    body: 'Every question drops into one of five colour bins, so you always know precisely where each concept stands.',
  },
  {
    n: '5',
    title: 'Unlock the right help',
    body: 'Stuck? A hint comes first. Still stuck? A short video or theory. Tough one? A guided step-by-step builds it up with you.',
  },
  {
    n: '6',
    title: 'Beat your past self',
    body: 'Concepts come back on a smart schedule and climb toward Green over time. The only rival here is yesterday’s you.',
  },
];

// The five bins, explained the way a student would say it.
const LADDER: { bin: DsBin; label: string; blurb: string }[] = [
  { bin: 'green', label: 'Green', blurb: 'Fast and correct — mastered.' },
  { bin: 'purple', label: 'Purple', blurb: 'Correct, just a little slow.' },
  { bin: 'blue', label: 'Blue', blurb: 'Got it on try two, with a hint.' },
  { bin: 'orange', label: 'Orange', blurb: 'Solved it with video + theory.' },
  { bin: 'pink', label: 'Pink', blurb: 'Tricky — we build it up together.' },
];

export function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-paper text-ink">
      {/* ── Slim brand bar ── */}
      <header className="mx-auto flex max-w-[1080px] items-center gap-3 px-5 py-4">
        <img src="/cwy-mark.svg" alt="CWY" width={36} height={36} style={{ borderRadius: '9px' }} />
        <span className="text-fs-15 font-semibold text-forest">CWY · Compete With Yourself</span>
        <button
          onClick={() => navigate('/login')}
          className="ml-auto text-sm font-medium text-ink-soft transition hover:text-forest"
        >
          Sign in →
        </button>
      </header>

      {/* ── Hero ── */}
      <section className="mx-auto max-w-[1080px] px-5 pb-10 pt-8 md:pt-16">
        <div className="max-w-2xl">
          <span
            className="inline-block rounded-full px-3 py-1 text-xs font-semibold"
            style={{ background: 'var(--green-bg)', color: 'var(--green-text)' }}
          >
            For Grade 10 & 12 · ICSE & CBSE
          </span>
          <h1 className="mt-4 text-4xl font-bold leading-tight text-forest md:text-5xl">
            Stop racing the class.<br />Start beating yesterday’s you.
          </h1>
          <p className="mt-4 text-lg text-ink-soft">
            CWY grades your board practice in seconds, shows you exactly where every concept stands, and
            walks you up — hint by hint — until it turns Green. No leaderboards. No comparing. Just you,
            getting sharper every week.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Button size="md" style={{ padding: '12px 22px', fontSize: fontPx(15) }} onClick={() => navigate('/signup')}>
              Start learning — it’s free
            </Button>
            <Button
              variant="secondary"
              style={{ padding: '12px 22px', fontSize: fontPx(15) }}
              onClick={() => navigate('/signup')}
            >
              Set up in 2 minutes
            </Button>
          </div>
          <p className="mt-3 text-xs text-ink-faint">Takes a 5-minute diagnostic to set your starting point. No marks are ever shared.</p>
        </div>
      </section>

      {/* ── How it works (the workflow, made plain) ── */}
      <section className="border-y border-line bg-paper-sunk">
        <div className="mx-auto max-w-[1080px] px-5 py-12">
          <h2 className="text-2xl font-bold text-forest">How it works</h2>
          <p className="mt-1 text-ink-soft">Six steps, every single time you practise.</p>

          <div className="mt-7 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {STEPS.map((s) => (
              <div key={s.n} className="rounded-card border border-line bg-paper p-5">
                <div
                  data-numeric=""
                  className="flex h-9 w-9 items-center justify-center rounded-full text-base font-bold text-white"
                  style={{ background: 'var(--brand-forest)' }}
                >
                  {s.n}
                </div>
                <h3 className="mt-3 text-fs-15 font-semibold text-ink">{s.title}</h3>
                <p className="mt-1.5 text-sm text-ink-soft">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── The five-bin ladder ── */}
      <section className="mx-auto max-w-[1080px] px-5 py-12">
        <h2 className="text-2xl font-bold text-forest">Where you land: the five colours</h2>
        <p className="mt-1 max-w-2xl text-ink-soft">
          Forget a single percentage. Every concept sits in a colour that tells you what to do next — and
          your job is simply to move each one toward Green.
        </p>

        <div className="mt-7 flex flex-col gap-3">
          {LADDER.map((b, i) => (
            <div
              key={b.bin}
              className="flex items-center gap-4 rounded-card border border-line bg-paper p-4"
            >
              <span data-numeric="" className="w-5 shrink-0 text-sm font-bold text-ink-faint">{i + 1}</span>
              <BinPill bin={b.bin} style={{ flex: 'none' }}>{b.label}</BinPill>
              <span className="text-sm text-ink-soft">{b.blurb}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── The promise / no-ranking ── */}
      <section className="border-t border-line bg-paper-sunk">
        <div className="mx-auto grid max-w-[1080px] gap-6 px-5 py-12 md:grid-cols-3">
          <Promise title="No leaderboards, ever" body="We never rank you against classmates. Your only benchmark is your own past performance." />
          <Promise title="Marks in seconds" body="Typed or handwritten, our AI grades the moment you submit — so you learn while it’s fresh." />
          <Promise title="Help that meets you" body="Hints, then video, then a guided scaffold. You always get exactly as much support as you need." />
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="mx-auto max-w-[1080px] px-5 py-16 text-center">
        <h2 className="text-3xl font-bold text-forest">Ready to compete with yourself?</h2>
        <p className="mx-auto mt-2 max-w-xl text-ink-soft">
          Set your board and grade, take a short diagnostic, and watch your concepts start climbing.
        </p>
        <div className="mt-6">
          <Button size="md" style={{ padding: '13px 26px', fontSize: fontPx(15) }} onClick={() => navigate('/signup')}>
            Start learning — it’s free
          </Button>
        </div>
      </section>

      <footer className="border-t border-line">
        <div className="mx-auto flex max-w-[1080px] flex-wrap items-center justify-between gap-2 px-5 py-6 text-xs text-ink-faint">
          <span>© CWY · Compete With Yourself</span>
          <span>Grade 10 & 12 · ICSE & CBSE</span>
        </div>
      </footer>
    </div>
  );
}

function Promise({ title, body }: { title: string; body: string }) {
  return (
    <div>
      <h3 className="text-base font-semibold text-forest">{title}</h3>
      <p className="mt-1.5 text-sm text-ink-soft">{body}</p>
    </div>
  );
}
